import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "bigradap.admin.token";

const AdminAuthContext = createContext(null);

export function AdminAuthProvider({ children }) {
  const [token, setTokenState] = useState(() => {
    if (typeof window === "undefined") return null;
    return window.localStorage.getItem(STORAGE_KEY);
  });
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(false);

  const setToken = useCallback((tk) => {
    setTokenState(tk);
    if (typeof window !== "undefined") {
      if (tk) window.localStorage.setItem(STORAGE_KEY, tk);
      else window.localStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  const logout = useCallback(() => {
    setToken(null);
    setAdmin(null);
  }, [setToken]);

  const login = useCallback(async (email, password) => {
    const { data } = await axios.post(`${API}/admin/login`, { email, password });
    setToken(data.token);
    setAdmin({ email: data.email, role: "admin" });
    return data;
  }, [setToken]);

  // Validate token on mount and whenever it changes
  useEffect(() => {
    let cancelled = false;
    async function check() {
      if (!token) {
        setAdmin(null);
        return;
      }
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/admin/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setAdmin(data);
      } catch (e) {
        if (!cancelled) {
          setAdmin(null);
          setToken(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    check();
    return () => {
      cancelled = true;
    };
  }, [token, setToken]);

  const authedFetch = useCallback(
    (path, options = {}) =>
      axios({
        url: `${API}${path}`,
        ...options,
        headers: {
          ...(options.headers || {}),
          Authorization: token ? `Bearer ${token}` : undefined,
        },
      }),
    [token]
  );

  const value = useMemo(
    () => ({ token, admin, login, logout, loading, authedFetch }),
    [token, admin, login, logout, loading, authedFetch]
  );

  return <AdminAuthContext.Provider value={value}>{children}</AdminAuthContext.Provider>;
}

export function useAdminAuth() {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) throw new Error("useAdminAuth must be inside AdminAuthProvider");
  return ctx;
}
